import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, QrCode, Landmark, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  custom_domain: "Custom domain",
  themes: "Public-page themes",
  analytics: "Analytics dashboard",
  unlimited_items: "Unlimited offers & categories",
};

function StatusBadge({ status }) {
  const map = {
    pending: { c: "bg-yellow-500/20 text-yellow-300", i: Clock, t: "Pending" },
    approved: { c: "bg-green-500/20 text-green-300", i: CheckCircle2, t: "Approved" },
    rejected: { c: "bg-red-500/20 text-red-300", i: XCircle, t: "Rejected" },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${s.c}`}><s.i className="w-3 h-3" />{s.t}</span>;
}

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [p, s, m] = await Promise.all([api.get("/billing/plans"), api.get("/billing/payment-settings"), api.get("/billing/my-submissions")]);
    setPlans(p.data.plans);
    setSettings(s.data || {});
    setSubmissions(m.data);
  };
  useEffect(() => { (async () => { try { await load(); } finally { setLoading(false); } })(); }, []);

  const submit = async () => {
    if (!selected) return toast.error("Select a plan");
    setSubmitting(true);
    try {
      await api.post("/billing/submit", { plan: selected.id, amount: selected.price, reference, proof_path: proof });
      setReference(""); setProof(""); setSelected(null);
      await load();
      toast.success("Payment submitted for verification");
    } catch {
      toast.error("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const sub = user?.subscription;
  if (loading) return (<div className="min-h-screen"><Navbar /><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></div>);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 py-8">
        <span className="overline text-primary">Billing</span>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Plans & payments</h1>

        {sub && (
          <div className="mt-4 text-sm text-muted-foreground" data-testid="current-plan">
            Current: <span className="text-white capitalize font-semibold">{sub.plan}</span> ({sub.status})
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {plans.map((p) => (
            <div key={p.id} onClick={() => setSelected(p)} className={`cursor-pointer bg-card border rounded-xl p-6 transition-colors ${selected?.id === p.id ? "border-primary" : "border-white/10 hover:border-white/25"}`} data-testid={`plan-${p.id}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold capitalize">{p.name}</h3>
                {selected?.id === p.id && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="mt-3"><span className="font-display text-3xl font-bold">Rs {Number(p.price).toLocaleString()}</span><span className="text-muted-foreground text-sm">{p.interval === "one_time" ? " one-time" : `/${p.interval}`}</span></div>
              <ul className="mt-3 space-y-1.5">
                <li className="flex items-center gap-2 text-xs text-muted-foreground"><Check className="w-3 h-3 text-primary shrink-0" /> Public page with categorized offers</li>
                {(p.modules || []).map((m) => (
                  <li key={m} className="flex items-center gap-2 text-xs" data-testid={`plan-${p.id}-feature-${m}`}>
                    <Check className="w-3 h-3 text-primary shrink-0" /> {MODULE_LABELS[m] || m}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Payment instructions */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <h3 className="font-display font-semibold flex items-center gap-2"><QrCode className="w-4 h-4 text-primary" /> How to pay</h3>
            {settings.qr_path ? (
              <img src={fileUrl(settings.qr_path)} alt="Payment QR" className="mt-4 w-44 h-44 object-contain rounded-lg bg-white p-2" data-testid="payment-qr" />
            ) : (
              <p className="text-sm text-muted-foreground mt-4">Payment QR will appear here once configured by the admin.</p>
            )}
            <div className="mt-4 space-y-1 text-sm">
              {[["JazzCash", settings.jazzcash], ["Easypaisa", settings.easypaisa], ["IBAN", settings.iban]].filter(([, v]) => v).map(([k, v]) => (
                <p key={k} className="flex items-center gap-2"><span className="text-muted-foreground">{k}:</span> <span className="font-mono">{v}</span>
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => { navigator.clipboard.writeText(v); toast.success(`${k} copied`); }}>copy</button></p>
              ))}
              {settings.upi_id && <p><span className="text-muted-foreground">UPI:</span> {settings.upi_id}</p>}
              {settings.bank_name && <p className="flex items-center gap-1"><Landmark className="w-3 h-3 text-primary" /> {settings.bank_name}</p>}
              {settings.account_name && <p><span className="text-muted-foreground">Name:</span> {settings.account_name}</p>}
              {settings.account_number && <p><span className="text-muted-foreground">A/C:</span> {settings.account_number}</p>}
              {settings.ifsc && <p><span className="text-muted-foreground">Branch code:</span> {settings.ifsc}</p>}
            </div>
            {settings.instructions && <p className="text-xs text-muted-foreground mt-3">{settings.instructions}</p>}
          </div>

          {/* Submit proof */}
          <div className="bg-card border border-white/10 rounded-xl p-6">
            <h3 className="font-display font-semibold">Submit your payment</h3>
            <p className="text-xs text-muted-foreground mt-1">Scan the QR / transfer, then upload your proof. Admin will verify and activate your plan.</p>
            <div className="mt-4 space-y-4">
              <div className="text-sm">Selected plan: {selected ? <span className="text-primary font-semibold capitalize">{selected.name} · Rs {Number(selected.price).toLocaleString()}</span> : <span className="text-muted-foreground">none</span>}</div>
              <div><Label className="overline text-muted-foreground">Transaction reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / txn id" data-testid="payment-reference" className="mt-2 bg-background border-white/10" /></div>
              <ImageUpload label="Payment screenshot" value={proof} onChange={setProof} testid="upload-proof" />
              <Button onClick={submit} disabled={submitting || !selected} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="submit-payment-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for verification"}
              </Button>
            </div>
          </div>
        </div>

        {/* My submissions */}
        <div className="mt-8">
          <h3 className="font-display font-semibold">Your submissions</h3>
          {submissions.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3" data-testid="no-submissions">No payment submissions yet.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {submissions.map((s) => (
                <div key={s.payment_id} className="bg-card border border-white/10 rounded-lg p-4 flex items-center justify-between" data-testid={`submission-${s.payment_id}`}>
                  <div>
                    <p className="text-sm capitalize font-medium">{s.plan} · Rs {Number(s.amount).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{new Date(s.submitted_at).toLocaleString()} {s.reference && `· ${s.reference}`}</p>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
